
import { useSEO } from '../hooks/useSEO';
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, cubicBezier } from 'framer-motion';
import { BookOpenIcon, LinkIcon, LockIcon, DownloadIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { blogPosts } from '@shared/data/blogData';
import { authors } from '@shared/data/authorsData';
import { mockRssFeeds } from '@shared/data/rssFeedsData';
import { booklets } from '../data/resourcesData';
import { AppPage, UserProfile, RssFeedItem } from '../types';
import { BlogPost } from '@shared/types';
import { CDN_URLS } from '@shared/config';
import { getImageUrl } from '../utils/imageUtils';
import { IndustryFeedCarousel } from '../components/IndustryFeedCarousel';
import Footer from '../components/Footer';
import SchemaMarkup from '../components/SchemaMarkup';
import { fetchDynamicRssFeeds, fetchDiaryPosts } from '../services/rssService';

interface BlogPageProps {
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
  userProfile?: UserProfile;
}

const VISIBLE_COUNT = 5; // Number of blog posts visible at once
const ROTATION_INTERVAL = 10000; // Auto-advance every 6 seconds

const BlogPage: React.FC<BlogPageProps> = ({ onNavigate, userProfile }) => {
  const { t } = useTranslation();
  const [rssFeeds, setRssFeeds] = useState<RssFeedItem[]>(mockRssFeeds);
  const [isRssLoading, setIsRssLoading] = useState(true);
  const [allPosts, setAllPosts] = useState<BlogPost[]>(blogPosts);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Fetch RSS sidebar feeds + diary blog posts
  useEffect(() => {
    const loadFeeds = async () => {
      try {
        const [dynamicFeeds, diaryPosts] = await Promise.all([
          fetchDynamicRssFeeds(),
          fetchDiaryPosts()
        ]);
        if (dynamicFeeds.length > 0) {
          setRssFeeds(dynamicFeeds);
        }
        if (diaryPosts.length > 0) {
          // Merge diary posts with static posts, diary first (newest)
          setAllPosts(prev => [...diaryPosts, ...prev]);
        }
      } catch (error) {
        console.error("Failed to fetch feeds:", error);
      } finally {
        setIsRssLoading(false);
      }
    };
    loadFeeds();
  }, []);

  // Auto-rotate blog posts
  useEffect(() => {
    if (isPaused || allPosts.length <= VISIBLE_COUNT) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % allPosts.length);
    }, ROTATION_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused, allPosts.length]);

  // Get the visible subset using circular indexing
  const visiblePosts = allPosts.length > 0
    ? Array.from({ length: Math.min(VISIBLE_COUNT, allPosts.length) }).map((_, i) => {
        const index = (currentIndex + i) % allPosts.length;
        return { post: allPosts[index], globalIndex: index };
      })
    : [];

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1), staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };


  useSEO(
    t('blogPage_seo_title'),
    t('blogPage_seo_desc'),
    t('blogPage_seo_keywords').split(',').map(k => k.trim())
  );

  // Check access level
  const hasAccess = userProfile?.membershipLevel === 'premium' || userProfile?.membershipLevel === 'advanced';

  const handleDownload = (bookletId: string, title: string) => {
    // Mock download action
    alert(`${t('booklet_downloading')} ${title}`);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
      className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 overflow-y-auto custom-scrollbar"
    >
      <div className="max-w-6xl w-full">
        <motion.div variants={itemVariants} className="flex flex-col items-center mb-12">
          <BookOpenIcon className="w-12 h-12 text-gray-700 dark:text-gray-200 mb-4" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white text-center leading-tight">
            {t('blogPage_title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mt-3 text-center max-w-2xl">
            {t('blogPage_tagline')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content: Blog Posts */}
          <div className="lg:col-span-2 space-y-12">

            {/* Exclusive Resources Section */}
            <motion.section variants={itemVariants} className="bg-indigo-50 dark:bg-indigo-900/20 p-6 rounded-2xl border border-indigo-100 dark:border-indigo-800">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-2xl font-serif font-semibold text-indigo-900 dark:text-indigo-100">{t('blogPage_resources_title')}</h2>
                {!hasAccess && <LockIcon className="w-5 h-5 text-gray-400" />}
              </div>

              {hasAccess ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {booklets.map((booklet: any) => (
                    <div key={booklet.id} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex gap-4">
                      <img src={booklet.coverImageUrl} alt={t(booklet.titleKey)} className="w-20 h-28 object-cover rounded-lg shadow-sm flex-shrink-0" />
                      <div className="flex flex-col justify-between">
                        <div>
                          <h3 className="font-bold text-gray-900 dark:text-white leading-tight mb-1">{t(booklet.titleKey)}</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{t(booklet.descriptionKey)}</p>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-500 dark:text-gray-300 uppercase">{t(`resource_category_${booklet.category.toLowerCase()}`)}</span>
                          <button
                            onClick={() => booklet.downloadUrl ? window.open(booklet.downloadUrl, '_blank') : handleDownload(booklet.id, t(booklet.titleKey))}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors flex items-center gap-1 text-sm font-semibold"
                          >
                            <DownloadIcon className="w-4 h-4" /> {t('common_pdf')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="relative">
                  {/* Blurred Preview */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 blur-sm opacity-40 pointer-events-none select-none">
                    {booklets.slice(0, 2).map((booklet: any) => (
                      <div key={booklet.id} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex gap-4">
                        <div className="w-20 h-28 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0" />
                        <div className="flex flex-col gap-2">
                          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                          <div className="h-3 w-40 bg-gray-100 dark:bg-gray-700 rounded" />
                          <div className="h-3 w-24 bg-gray-100 dark:bg-gray-700 rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Upgrade Overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                    <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md p-8 rounded-3xl shadow-2xl border border-indigo-100 dark:border-indigo-900/50 max-w-sm">
                      <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/50 rounded-full flex items-center justify-center mb-6 mx-auto">
                        <LockIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <h3 className="text-xl font-serif font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
                        {t('blogPage_resources_locked_title') || "Unlock Premium Content"}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                        {t('blogPage_resources_locked_msg')}
                      </p>
                      <button
                        onClick={() => onNavigate('profile')}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black py-4 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-indigo-500/30 transform active:scale-95 uppercase tracking-wider text-sm"
                      >
                        {t('blogPage_resources_upgrade')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.section>

            <motion.section
              variants={sectionVariants}
              className="space-y-8 relative"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Rotation indicator */}
              {allPosts.length > VISIBLE_COUNT && (
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {currentIndex + 1}–{Math.min(currentIndex + VISIBLE_COUNT, allPosts.length)} / {allPosts.length} {t('blog_post_read_more') ? 'articles' : 'articles'}
                  </p>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.ceil(allPosts.length / VISIBLE_COUNT) }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i * VISIBLE_COUNT % allPosts.length)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          Math.floor(currentIndex / VISIBLE_COUNT) === i
                            ? 'bg-indigo-600 w-4'
                            : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'
                        }`}
                        aria-label={`Go to page ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              <AnimatePresence mode="popLayout">
                {visiblePosts.map(({ post, globalIndex }) => {
                  if (!post) return null;
                  const isExternal = post.isExternal;
                  const postTitle = isExternal ? post.title : t(post.titleKey);
                  const postExcerpt = isExternal ? post.excerpt : t(post.excerptKey);
                  const author = !isExternal ? authors.find((a: any) => a && a.id === post.authorId) : null;

                  return (
                    <motion.div
                      key={`${post.id}-${globalIndex}`}
                      layout
                      initial={{ opacity: 0, y: 30, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -30, scale: 0.97 }}
                      transition={{ duration: 0.5, ease: 'easeInOut' }}
                      className="group bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                        <div className="md:col-span-1 rounded-lg overflow-hidden">
                          {post.imageUrl ? (
                            <img
                              src={getImageUrl(post.imageUrl)}
                              alt={postTitle || ''}
                              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="w-full h-48 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 flex items-center justify-center rounded-lg">
                              <BookOpenIcon className="w-12 h-12 text-indigo-300 dark:text-indigo-600" />
                            </div>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            {isExternal
                              ? <><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 mr-2">Diary</span>{post.date}</>
                              : <>{t(post.dateKey || '')}{author && <span> &bull; {t('blog_byline')}<button onClick={() => onNavigate('author', { authorId: author.id })} className="hover:underline">{t(author.nameKey)}</button></span>}</>}
                          </p>
                          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                            {isExternal ? (
                              <a href={post.externalUrl} target="_blank" rel="noopener noreferrer" className="text-left hover:underline">
                                {postTitle}
                              </a>
                            ) : (
                              <button onClick={() => onNavigate('blog-post', { postId: post.id })} className="text-left">
                                {postTitle}
                              </button>
                            )}
                          </h2>
                          <p className="text-gray-700 dark:text-gray-300 leading-relaxed line-clamp-3">
                            {postExcerpt}
                          </p>
                          {post.externalUrl ? (
                            <a href={post.externalUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 font-semibold mt-4 inline-flex items-center gap-1 hover:underline">
                              {t('blog_post_read_more')} <LinkIcon className="w-3.5 h-3.5" /> &rarr;
                            </a>
                          ) : (
                            <button onClick={() => onNavigate('blog-post', { postId: post.id })} className="text-indigo-600 dark:text-indigo-400 font-semibold mt-4 inline-block hover:underline">
                              {t('blog_post_read_more')} &rarr;
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.section>
          </div>

          {/* Sidebar: RSS Feeds Carousel */}
          <aside className="lg:col-span-1">
            <motion.div variants={itemVariants} className="sticky top-24 bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-2">{t('editorial_rss_title')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{t('editorial_rss_subtitle')}</p>

              {isRssLoading ? (
                <div className="flex justify-center items-center h-[420px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <IndustryFeedCarousel items={rssFeeds} />
              )}

            </motion.div>
          </aside>
        </div>
      </div>
      <SchemaMarkup
        type="Blog"
        data={{
          name: t('blogPage_title'),
          description: t('blogPage_tagline'),
          blogPost: blogPosts.map(post => ({
            "@type": "BlogPosting",
            headline: t(post.titleKey),
            description: t(post.excerptKey),
            image: getImageUrl(post.imageUrl),
            datePublished: post.date,
            dateModified: post.dateModified || post.date,
            author: {
              "@type": "Person",
              name: authors.find(a => a.id === post.authorId)?.nameKey ? t(authors.find(a => a.id === post.authorId)?.nameKey || '') : "Constantina Team"
            },
            url: `${window.location.origin}/blog-post/${post.id}`
          }))
        }}
      />
      <Footer onNavigate={onNavigate} />
    </motion.div>
  );
};

export default BlogPage;
